import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { siteConfig } from '../data/siteConfig';
import { pageMetadata } from '../data/pageMetadata';

const SEOHead = ({ 
  title, 
  description, 
  keywords,
  image,
  type = "website",
  schema,
  noindex = false,
  canonical,
  hreflang,
  children 
}) => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  // Auto-detect page metadata if not provided
  const getPageMetadata = (path) => {
    if (path === '/') return pageMetadata.home;
    if (path === '/about') return pageMetadata.about;
    if (path === '/contact') return pageMetadata.contact;
    if (path === '/blog') return pageMetadata.blog;
    if (path === '/quote') return pageMetadata.quote;
    if (path === '/faq') return pageMetadata.faq;
    if (path.startsWith('/services/sme')) return pageMetadata.services.sme;
    if (path.startsWith('/services/large-enterprise')) return pageMetadata.services.largeEnterprise;
    if (path.startsWith('/services/consultancy')) return pageMetadata.services.consultancy;
    if (path.startsWith('/success-stories/sme')) return pageMetadata.successStories.sme;
    if (path.startsWith('/success-stories/large-enterprise')) return pageMetadata.successStories.largeEnterprise;
    if (path.startsWith('/success-stories/consultancy')) return pageMetadata.successStories.consultancy;
    return null;
  };

  const pageData = getPageMetadata(currentPath);
  
  const seoTitle = title || pageData?.title || siteConfig.title;
  const seoDescription = description || pageData?.description || siteConfig.description;
  const seoKeywords = keywords || pageData?.keywords || siteConfig.keywords.join(', ');
  const seoImage = image || (pageData?.image ? `${siteConfig.url}${pageData.image}` : `${siteConfig.url}${siteConfig.ogImage}`);
  const seoCanonical = canonical || `${siteConfig.url}${currentPath}`;
  const seoType = type || pageData?.type || "website";

  return (
    <Helmet>
      {/* Preload primary hero image for faster LCP */}
      {pageData?.image && (
        <link rel="preload" as="image" href={pageData.image} />
      )}
      {/* Basic Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="author" content={siteConfig.creator} />
      <meta name="language" content={siteConfig.language} />
      <link rel="canonical" href={seoCanonical} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={seoType} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${seoTitle} - ${siteConfig.name}`} />
      <meta property="og:url" content={seoCanonical} />
      <meta property="og:site_name" content={siteConfig.name} />
      <meta property="og:locale" content={siteConfig.locale} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      <meta name="twitter:image:alt" content={seoTitle} />
      <meta name="twitter:creator" content="@globalpayrollmig" />
      <meta name="twitter:site" content="@globalpayrollmig" />
      
      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#1a237e" />
      <meta name="msapplication-TileColor" content="#1a237e" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={siteConfig.name} />
      
      {/* Geo Meta Tags */}
      <meta name="geo.region" content="GB" />
      <meta name="geo.placename" content="London" />
      <meta name="geo.position" content="51.5074;-0.1278" />
      <meta name="ICBM" content="51.5074, -0.1278" />
      
      {/* Business Meta Tags */}
      <meta name="classification" content="Business Services" />
      <meta name="category" content="Payroll Migration Services" />
      <meta name="coverage" content="United Kingdom" />
      <meta name="distribution" content="Global" />
      <meta name="rating" content="General" />
      
      {/* Hreflang */}
      {hreflang && hreflang.map((lang, index) => (
        <link key={index} rel="alternate" hreflang={lang.lang} href={lang.href} />
      ))}
      
      {/* Additional head content */}
      {children}
    </Helmet>
  );
};

export default SEOHead;
